import type { ItemTypeEnum, ToothScopedItem } from "./items.types";

export const WaxUpTypeEnum = {
  PHYSICAL: "PHYSICAL",
  NUMERICAL: "NUMERICAL",
} as const;

export type WaxUpType = (typeof WaxUpTypeEnum)[keyof typeof WaxUpTypeEnum];

export interface WaxUpItem extends ToothScopedItem {
  type: typeof ItemTypeEnum.waxUp;
  materialCharacteristics?: undefined;
}

export interface WaxUpTypeConfiguration extends WaxUpItem {
  waxUpType: WaxUpType;
}

export const WaxUpTypeSelectionEnum = {
  NUMERICAL: WaxUpTypeEnum.NUMERICAL,
  PHYSICAL: WaxUpTypeEnum.PHYSICAL,
  NUMERICAL_AND_PHYSICAL: "NUMERICAL_AND_PHYSICAL",
} as const;

export type WaxUpTypeSelection =
  (typeof WaxUpTypeSelectionEnum)[keyof typeof WaxUpTypeSelectionEnum];

/**
 * Deduce the wax-up type selection from a product's items: v2 stores one item
 * per tooth and type, v1 may store NUMERICAL_AND_PHYSICAL on a single item.
 */
export const getWaxUpTypeSelection = (
  items: ReadonlyArray<{ waxUpType?: string | null }>
): WaxUpTypeSelection | undefined => {
  const waxUpTypes = new Set(items.map(item => item.waxUpType));

  if (waxUpTypes.has(null) || waxUpTypes.has(undefined)) {
    return undefined;
  }

  const hasNumerical = waxUpTypes.has(WaxUpTypeEnum.NUMERICAL);
  const hasPhysical = waxUpTypes.has(WaxUpTypeEnum.PHYSICAL);

  if (
    waxUpTypes.has(WaxUpTypeSelectionEnum.NUMERICAL_AND_PHYSICAL) ||
    (hasNumerical && hasPhysical)
  ) {
    return WaxUpTypeSelectionEnum.NUMERICAL_AND_PHYSICAL;
  }

  if (hasNumerical) {
    return WaxUpTypeEnum.NUMERICAL;
  }

  if (hasPhysical) {
    return WaxUpTypeEnum.PHYSICAL;
  }

  return undefined;
};
